import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { REQUEST_API_BASE } from '../utils/publicUrl';

/** One saved slug, as the status route answered it. */
export type EndpointStatus = {
  slug: string;
  exists: boolean;
  /** Captured rows the endpoint currently holds. */
  requestCount: number;
};

/**
 * What the status route answered, keyed by slug.
 *
 * `null` means the list is *unknown*: the call is still in flight, there was
 * nothing to ask about, or it failed. Unknown is deliberately not the same as
 * expired — every caller must render an unknown row exactly as it did before
 * this route existed, because a network blip that marked a working endpoint
 * dead would be worse than never checking at all.
 */
export type EndpointStatusMap = Map<string, EndpointStatus>;

const STATUS_PATH = '/manage/endpoints/status';

/**
 * How many slugs one status request may carry.
 *
 * Mirrors the route's own ceiling (`MAX_STATUS_SLUGS` in
 * `backend/src/common/endpoint-status.ts`): past this many slugs the route
 * answers 400. Nothing caps how many endpoints a browser may save, so the
 * saved list is *batched* into requests of this size instead of assuming it
 * fits in one. A single over-long call would 400, which is read as "unknown"
 * and would silently switch every Expired badge off for the heaviest users.
 */
export const STATUS_SLUGS_PER_REQUEST = 50;

/** Split the saved slugs into request-sized groups, keeping their order. */
const inBatches = (slugs: string[]): string[][] => {
  const batches: string[][] = [];

  for (let start = 0; start < slugs.length; start += STATUS_SLUGS_PER_REQUEST) {
    batches.push(slugs.slice(start, start + STATUS_SLUGS_PER_REQUEST));
  }

  return batches;
};

/** Only a well-formed answer from the route counts as knowing something. */
const toStatusMap = (data: unknown): EndpointStatusMap => {
  const map: EndpointStatusMap = new Map();
  const entries =
    data && typeof data === 'object' && Array.isArray((data as any).endpoints)
      ? (data as any).endpoints
      : [];

  for (const entry of entries) {
    if (!entry || typeof entry.slug !== 'string') {
      continue;
    }

    const requestCount = Number(entry.requestCount);

    map.set(entry.slug, {
      slug: entry.slug,
      exists: entry.exists === true,
      requestCount: Number.isFinite(requestCount) && requestCount > 0 ? requestCount : 0,
    });
  }

  return map;
};

/**
 * Whether the saved endpoints still exist, asked once when the list loads.
 *
 * The saved list travels as one request per ≤50 slugs (`?slugs=a,b,c`), all
 * issued together once per mount: the question is about the list as the page
 * loaded, so renaming or removing a row does not re-ask, and neither does
 * returning to the tab. That also keeps the answer stable — a row's verdict
 * cannot flicker to "unknown" while a follow-up request is in flight, because
 * the single state write happens only after every batch has settled.
 *
 * Each batch is judged on its own. A slug is expired only if the batch that
 * asked about it answered that it does not exist; a slug whose batch failed
 * stays unknown. That is why the batches are merged with `allSettled` rather
 * than `all`: `all` would reject on the first failure and throw away the
 * answers from every batch that did succeed, turning one failed request into
 * the whole-list-blank bug this batching exists to prevent. If *every* batch
 * fails the answer is unknown, exactly as a single failed call was.
 *
 * The slugs are read from a ref rather than the render argument because the
 * effect deliberately runs once; a failed check leaves the answer unknown
 * instead of expired.
 */
export function useEndpointStatus(slugs: string[]): EndpointStatusMap | null {
  const [statuses, setStatuses] = useState<EndpointStatusMap | null>(null);
  const initialSlugs = useRef(slugs);

  useEffect(() => {
    const asked = initialSlugs.current;
    const slugsKey = asked.join(',');

    if (slugsKey === '') {
      // Nothing saved: there is no question to ask, so no call is made.
      setStatuses(null);
      return;
    }

    let cancelled = false;

    Promise.allSettled(
      inBatches(asked).map((batch) =>
        axios.get(`${REQUEST_API_BASE}${STATUS_PATH}`, { params: { slugs: batch.join(',') } }),
      ),
    ).then((results) => {
      if (cancelled) {
        return;
      }

      let answered = 0;
      const merged: EndpointStatusMap = new Map();

      for (const result of results) {
        if (result.status === 'rejected') {
          // A failed batch is not evidence: the rows it asked about stay
          // unknown, while every batch that did answer keeps its verdict.
          console.warn('Failed to check whether saved endpoints still exist', result.reason);
          continue;
        }

        answered += 1;

        toStatusMap(result.value?.data).forEach((status, slug) => {
          merged.set(slug, status);
        });
      }

      if (answered === 0) {
        // Not one batch answered, so nothing is known — the same outcome as
        // today's single failed call.
        setStatuses(null);
        return;
      }

      // An answer that names none of the asked-about slugs tells us nothing,
      // so it leaves the list unknown rather than resolving to a verdict
      // about nothing. Applied to the merged result, not per batch: one
      // silent batch must not discard the batches that named their slugs.
      if (merged.size === 0) {
        return;
      }

      setStatuses(merged);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return statuses;
}
